import {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {apiFetch} from '../api';
import ShareActivityLog from './ShareActivityLog';
import {LockIcon} from './icons';

function formatDate(dateStr) {
    if (!dateStr) return 'Not yet opened';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
}

export default function ShareDialog({checklistId, onClose, onSharesChanged}) {
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [access, setAccess] = useState('edit');
    const [pin, setPin] = useState('');
    const [creating, setCreating] = useState(false);
    const [copied, setCopied] = useState(null);
    const [error, setError] = useState(null);
    const [view, setView] = useState('shares');
    const [editingPin, setEditingPin] = useState(null);
    const [editPinValue, setEditPinValue] = useState('');

    const fetchShares = useCallback(async () => {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/shares`);
            const data = await res.json();
            setShares(data);
            setLoading(false);
        } catch {
            setLoading(false);
        }
    }, [checklistId]);

    useEffect(() => {
        fetchShares();
    }, [fetchShares]);

    async function handleCreate() {
        const trimmed = name.trim();
        if (!trimmed || creating) return;
        setCreating(true);
        setError(null);
        try {
            const body = {name: trimmed, access};
            if (pin) body.pin = pin;
            const res = await apiFetch(`/checklists/${checklistId}/shares`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            });
            const share = await res.json();
            const nextShares = [...shares, share];
            setShares(nextShares);
            setName('');
            setPin('');
            if (onSharesChanged) onSharesChanged(nextShares.length);
        } catch (err) {
            setError(err.message || 'Failed to create share');
        } finally {
            setCreating(false);
        }
    }

    async function handleRevoke(shareId) {
        try {
            await apiFetch(`/checklists/${checklistId}/shares/${shareId}`, {method: 'DELETE'});
            const nextShares = shares.filter(s => s.id !== shareId);
            setShares(nextShares);
            if (onSharesChanged) onSharesChanged(nextShares.length);
        } catch {
            // silently fail
        }
    }

    async function handleUpdateShare(shareId, updates) {
        try {
            const res = await apiFetch(`/checklists/${checklistId}/shares/${shareId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(updates),
            });
            const updated = await res.json();
            setShares(prev =>
                prev.map(s =>
                    s.id === shareId ? {...s, accessLevel: updated.accessLevel, hasPin: updated.hasPin} : s,
                ),
            );
        } catch {
            // silently fail
        }
    }

    function handleSavePin(shareId) {
        if (editPinValue && (editPinValue.length < 4 || editPinValue.length > 6)) return;
        if (editPinValue) {
            handleUpdateShare(shareId, {pin: editPinValue});
        } else {
            handleUpdateShare(shareId, {removePin: true});
        }
        setEditingPin(null);
        setEditPinValue('');
    }

    function copyLink(token) {
        const url = `${window.location.origin}/s/${token}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(token);
            setTimeout(() => setCopied(null), 2000);
        });
    }

    return (
        <div
            className="modal-backdrop"
            onClick={onClose}
            onKeyDown={e => e.key === 'Escape' && onClose()}
            role="presentation"
        >
            <div
                className="modal share-dialog"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <h3 className="modal-title">Share checklist</h3>

                <div className="share-dialog-tabs">
                    <button
                        className={`share-dialog-tab${view === 'shares' ? ' active' : ''}`}
                        onClick={() => setView('shares')}
                    >
                        Shares
                    </button>
                    <button
                        className={`share-dialog-tab${view === 'activity' ? ' active' : ''}`}
                        onClick={() => setView('activity')}
                    >
                        Activity
                    </button>
                </div>

                {view === 'activity' ? (
                    <ShareActivityLog checklistId={checklistId} />
                ) : (
                    <>
                        <div className="share-create-form">
                            <input
                                className="modal-input"
                                type="text"
                                placeholder="Person's name (e.g. Mom)"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                                autoFocus
                            />
                            <select
                                className="share-access-select"
                                value={access}
                                onChange={e => setAccess(e.target.value)}
                                aria-label="Access level"
                            >
                                <option value="edit">Can edit</option>
                                <option value="read">View only</option>
                            </select>
                            <input
                                className="modal-input share-pin-input"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                placeholder="PIN (optional)"
                                value={pin}
                                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                aria-label="PIN"
                            />
                            <button
                                className="modal-confirm"
                                onClick={handleCreate}
                                disabled={
                                    !name.trim() || creating || (pin !== '' && (pin.length < 4 || pin.length > 6))
                                }
                            >
                                Create link
                            </button>
                        </div>
                        <p className="share-warning">
                            Anyone with this link can {access === 'edit' ? 'edit' : 'view'} this checklist.
                            {pin ? ' They will need the PIN to access it.' : ' They don\u2019t need an account.'}
                        </p>

                        {error && <p className="share-error">{error}</p>}

                        {loading ? (
                            <p className="share-loading">Loading shares...</p>
                        ) : shares.length > 0 ? (
                            <div className="share-list">
                                {shares.map(share => (
                                    <div key={share.id} className="share-list-item">
                                        <div className="share-list-info">
                                            <span className="share-list-name">
                                                {share.name}
                                                {share.hasPin && (
                                                    <span
                                                        className="share-pin-badge"
                                                        title="PIN protected"
                                                        aria-label={`PIN protected: ${share.name}`}
                                                    >
                                                        <LockIcon size={12} />
                                                    </span>
                                                )}
                                            </span>
                                            <select
                                                className="share-list-access"
                                                value={share.accessLevel}
                                                onChange={e => handleUpdateShare(share.id, {access: e.target.value})}
                                                aria-label={`Access for ${share.name}`}
                                            >
                                                <option value="edit">Edit</option>
                                                <option value="read">View</option>
                                            </select>
                                            <span className="share-list-meta">
                                                {share.viewCount} view{share.viewCount !== 1 ? 's' : ''}
                                                {' · '}
                                                {formatDate(share.firstViewedAt)}
                                            </span>
                                        </div>
                                        <div className="share-list-actions">
                                            <button
                                                className="share-pin-toggle-btn"
                                                onClick={() => {
                                                    if (editingPin === share.id) {
                                                        setEditingPin(null);
                                                        setEditPinValue('');
                                                    } else {
                                                        setEditingPin(share.id);
                                                        setEditPinValue('');
                                                    }
                                                }}
                                                aria-label={`${share.hasPin ? 'Change' : 'Set'} PIN for ${share.name}`}
                                            >
                                                {share.hasPin ? 'Change PIN' : 'Set PIN'}
                                            </button>
                                            <button
                                                className="share-copy-btn"
                                                onClick={() => copyLink(share.token)}
                                                aria-label={`Copy link for ${share.name}`}
                                            >
                                                {copied === share.token ? 'Copied!' : 'Copy link'}
                                            </button>
                                            <a
                                                className="share-preview-btn"
                                                href={`/s/${share.token}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Preview share for ${share.name}`}
                                            >
                                                Preview
                                            </a>
                                            <button
                                                className="share-revoke-btn"
                                                onClick={() => handleRevoke(share.id)}
                                                aria-label={`Revoke share for ${share.name}`}
                                            >
                                                Revoke
                                            </button>
                                        </div>
                                        {editingPin === share.id && (
                                            <div className="share-pin-edit">
                                                <input
                                                    className="modal-input share-pin-input"
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    maxLength={6}
                                                    placeholder={
                                                        share.hasPin ? 'New PIN (4-6 digits)' : 'Enter PIN (4-6 digits)'
                                                    }
                                                    value={editPinValue}
                                                    onChange={e =>
                                                        setEditPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))
                                                    }
                                                    onKeyDown={e => e.key === 'Enter' && handleSavePin(share.id)}
                                                    autoFocus
                                                    aria-label={`PIN input for ${share.name}`}
                                                />
                                                <button
                                                    className="modal-confirm share-pin-save-btn"
                                                    onClick={() => handleSavePin(share.id)}
                                                    disabled={
                                                        editPinValue !== '' &&
                                                        (editPinValue.length < 4 || editPinValue.length > 6)
                                                    }
                                                >
                                                    {editPinValue ? 'Save' : 'Remove PIN'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="share-empty">No shares yet. Create one above.</p>
                        )}
                    </>
                )}

                <div className="modal-actions">
                    <button className="modal-cancel" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

ShareDialog.propTypes = {
    checklistId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onSharesChanged: PropTypes.func,
};
